import { DndContext, DragOverlay } from "@dnd-kit/core";
import { Flex } from "@mantine/core";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { spacing } from "#app/theme/v2/spacing";
import { Company } from "#modules/company/infra/Company.dto";
import { PART_LEVELS_PRESETS } from "#modules/company/view/DefaultProcessStepFormModal";
import { useCompany } from "#modules/company/view/useCompany";
import { useDefaultProcessStepsDrag } from "#modules/company/view/useDefaultProcessStepsDrag";
import { useCreateDefaultProcessStep } from "#modules/defaultProcessSteps/view/useCreateDefaultProcessStep";
import { useDeleteDefaultProcessStep } from "#modules/defaultProcessSteps/view/useDeleteDefaultProcessStep";
import { useReorderDefaultProcessSteps } from "#modules/defaultProcessSteps/view/useReorderDefaultProcessSteps";
import {
  getProcessStepOptions,
  type ProcessStepOption,
} from "#modules/onboardingV2/domain/getProcessStepOptions";
import { getReorderedDefaultProcessStepIndexes } from "#modules/onboardingV2/domain/reorderDefaultProcessSteps";
import {
  computeNextDefaultProcessStepIndex,
  type DefaultProcessStepSection,
  type DefaultProcessStepSections,
  splitDefaultProcessSteps,
} from "#modules/onboardingV2/domain/splitDefaultProcessSteps";
import { DefaultProcessStepDragOverlay } from "#modules/onboardingV2/view/defaultProcessSteps/DefaultProcessStepItem";
import { DefaultProcessStepsColumn } from "#modules/onboardingV2/view/defaultProcessSteps/DefaultProcessStepsColumn";
import { useDeletableMaterialRows } from "#modules/onboardingV2/view/materialSpecification/useDeletableMaterialRows";
import { OnboardingV2Shell } from "#modules/onboardingV2/view/OnboardingV2Shell";
import { useOnboardingProcessSteps } from "#modules/onboardingV2/view/processStepConfiguration/useOnboardingProcessSteps";
import {
  useOnboardingAnalytics,
  useTrackOnboardingStepViewed,
} from "#modules/onboardingV2/view/useOnboardingAnalytics";
import { useOnboardingStepNavigation } from "#modules/onboardingV2/view/useOnboardingStepNavigation";
import { AnalyticsEvent } from "#shared/useAnalytics";

type DefaultProcessStep = Company["companySettings"]["defaultProcessSteps"][number];

export const OnboardingDefaultProcessStepsStepPage = () => {
  const { t } = useTranslation();
  const { company } = useCompany();
  const { position, total, goToNext, goToPrevious } =
    useOnboardingStepNavigation("defaultProcessSteps");
  const defaultProcessSteps = company?.companySettings.defaultProcessSteps ?? [];
  const { processSteps: onboardingProcessSteps } = useOnboardingProcessSteps();
  const { createDefaultProcessStep, isPending } = useCreateDefaultProcessStep();
  const { deleteDefaultProcessStep } = useDeleteDefaultProcessStep();
  const { reorderDefaultProcessSteps } = useReorderDefaultProcessSteps();

  useTrackOnboardingStepViewed("defaultProcessSteps");
  const { captureOnboardingEvent } = useOnboardingAnalytics("defaultProcessSteps");

  const { visibleRows, deleteRow, commitPendingRowDeletes } = useDeletableMaterialRows({
    rows: defaultProcessSteps,
    getRowId: (processStep) => processStep.id,
    getDeleteToastMessage: (count) =>
      t("onboardingV2.defaultProcessSteps.deleteToast.message", { count }),
    getUndoToastMessage: (count) =>
      t("onboardingV2.defaultProcessSteps.undoToast.message", { count }),
    onConfirmDelete: (processStep) => deleteDefaultProcessStep({ id: processStep.id }),
    onConfirmBulkDelete: (processSteps) =>
      processSteps.forEach((processStep) => deleteDefaultProcessStep({ id: processStep.id })),
  });

  const { beginning, end } = splitDefaultProcessSteps(visibleRows);

  const beginningOptions = useMemo(
    () => getProcessStepOptions(onboardingProcessSteps, beginning),
    [onboardingProcessSteps, beginning],
  );

  const endOptions = useMemo(
    () => getProcessStepOptions(onboardingProcessSteps, end),
    [onboardingProcessSteps, end],
  );

  const handleAdd = (
    section: DefaultProcessStepSection,
    option: ProcessStepOption,
    onSuccess: () => void,
  ) => {
    const processSteps = section === "beginning" ? beginning : end;

    const onboardingProcessStep = onboardingProcessSteps.find((step) => step.id === option.value);
    if (!onboardingProcessStep) {
      return;
    }

    createDefaultProcessStep(
      {
        payload: {
          industrialProcess: onboardingProcessStep.industrialProcess,
          description: option.description,
          index: computeNextDefaultProcessStepIndex(processSteps, section),
          partLevels: PART_LEVELS_PRESETS.ALL,
          preparationTimeFormula: onboardingProcessStep.preparationTimeFormula,
          cycleTimeFormula: onboardingProcessStep.cycleTimeFormula,
          machineHourlyPrice: onboardingProcessStep.price,
        },
      },
      {
        onSuccess: () => {
          captureOnboardingEvent(AnalyticsEvent.OnboardingDefaultProcessStepAdded, {
            section,
            industrialProcess: onboardingProcessStep.industrialProcess,
          });
          onSuccess();
        },
      },
    );
  };

  const handleReorderSections = useCallback(
    (nextSections: DefaultProcessStepSections<DefaultProcessStep>) => {
      reorderDefaultProcessSteps([
        ...getReorderedDefaultProcessStepIndexes(nextSections.beginning, "beginning"),
        ...getReorderedDefaultProcessStepIndexes(nextSections.end, "end"),
      ]);
    },
    [reorderDefaultProcessSteps],
  );

  const {
    sections,
    activeStep,
    sensors,
    collisionDetection,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    handleDragCancel,
  } = useDefaultProcessStepsDrag({
    beginning,
    end,
    onCommit: handleReorderSections,
  });

  const handleContinue = () => {
    commitPendingRowDeletes();
    goToNext();
  };

  return (
    <OnboardingV2Shell size="lg">
      <OnboardingV2Shell.Header
        onBack={goToPrevious}
        step={position}
        total={total}
        title={t("onboardingV2.defaultProcessSteps.title")}
        subtitle={t("onboardingV2.defaultProcessSteps.subtitle")}
      />

      <OnboardingV2Shell.Content overflow="hidden">
        <DndContext
          sensors={sensors}
          collisionDetection={collisionDetection}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <Flex
            gap={spacing[8]}
            align="stretch"
            style={{ flex: 1, minHeight: 0, overflow: "hidden" }}
          >
            <DefaultProcessStepsColumn
              title={t("onboardingV2.defaultProcessSteps.beginningTitle")}
              section="beginning"
              processSteps={sections.beginning}
              options={beginningOptions}
              isAddPending={isPending}
              onAddClick={() =>
                captureOnboardingEvent(AnalyticsEvent.OnboardingAddDefaultProcessStepClicked, {
                  section: "beginning",
                })
              }
              onAdd={(option, onSuccess) => handleAdd("beginning", option, onSuccess)}
              onDelete={deleteRow}
            />
            <DefaultProcessStepsColumn
              title={t("onboardingV2.defaultProcessSteps.endTitle")}
              section="end"
              processSteps={sections.end}
              options={endOptions}
              isAddPending={isPending}
              onAddClick={() =>
                captureOnboardingEvent(AnalyticsEvent.OnboardingAddDefaultProcessStepClicked, {
                  section: "end",
                })
              }
              onAdd={(option, onSuccess) => handleAdd("end", option, onSuccess)}
              onDelete={deleteRow}
            />
          </Flex>
          <DragOverlay>
            {activeStep ? <DefaultProcessStepDragOverlay processStep={activeStep} /> : null}
          </DragOverlay>
        </DndContext>
      </OnboardingV2Shell.Content>

      <OnboardingV2Shell.Footer
        buttonLabel={t("onboardingV2.continue")}
        onContinue={handleContinue}
        stepper={{ total, current: position }}
      />
    </OnboardingV2Shell>
  );
};
